import { queryOptions, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';

import { createProgressToast } from '@/components/common/progress-toast';
import type { AccessToken } from '@/lib/tokenStorage';
import {
  FertilizationQCResponse,
  ShipGroupListResponse,
  TOA_GetFreshShipmentFertList,
  TOA_GetFreshShipmentShipGroupMasters,
} from '@/rtk-query.gql';
import { useAccessToken } from '@/services/auth';

import {
  isAbortError,
  ProgressStreamError,
  readProgressStream,
} from '../progress-stream';
import {
  CombineShipmentsRequest,
  EquipmentItem,
  FreshShipmentListItem,
  FreshStrawFertilizationV2,
  MatingLabelsRequest,
  MutationResult,
  SendCommDairyEmailRequest,
  SignOffRequest,
  UpdateDSFieldRequest,
  UpdateFertFieldRequest,
  UpdateFieldRequest,
  UpdateIncubatorAssignmentRequest,
  UpdateQcRequest,
} from './types';

export type { UpdateQcRequest as UpdateQcFreshShipmentInput } from './types';
export type { FreshStrawFertilizationV2 } from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchGet<T>(
  token: AccessToken,
  path: string,
  params?: URLSearchParams,
): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/FreshShipment/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/FreshShipment/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (response.status === 204) return [] as unknown as T;
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return (await response.json()) as T;
}

async function fetchFreshStrawFertilizationsV2(
  token: AccessToken,
  params: {
    fertilizationDate: string;
    fertilizationSite: string;
    freshShipmentId: string;
  },
): Promise<FreshStrawFertilizationV2[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', params.fertilizationDate);
  if (params.fertilizationSite)
    searchParams.set('fertilizationSite', params.fertilizationSite);
  if (params.freshShipmentId)
    searchParams.set('freshShipmentId', params.freshShipmentId);

  const url = `${getBaseUrl()}/api/v2/fresh-straw/fertilizations?${searchParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (response.status === 204) return [];
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return (await response.json()) as FreshStrawFertilizationV2[];
}

async function fetchBlob(
  token: AccessToken,
  path: string,
  params?: URLSearchParams,
): Promise<Blob> {
  const url = params
    ? `${getBaseUrl()}/api/v1/FreshShipment/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/FreshShipment/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.blob();
}

async function fetchBlobReport(
  token: AccessToken,
  path: string,
  body?: unknown,
): Promise<Blob> {
  const url = `${getBaseUrl()}/api/v1/reports/${path}`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: getHeaders(token),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return response.blob();
}

async function fetchMutation<T>(
  token: AccessToken,
  path: string,
  method: 'POST' | 'PUT',
  body?: unknown,
): Promise<T> {
  const url = `${getBaseUrl()}/api/v1/FreshShipment/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(token),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
  return (await response.json()) as T;
}

async function fetchShipGroupList(
  token: AccessToken,
  params: {
    fertilizationDate: string;
    fertilizationSiteId: string;
  },
): Promise<ShipGroupListResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', params.fertilizationDate);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  return fetchGet<ShipGroupListResponse>(
    token,
    'ship-group-list',
    searchParams,
  );
}

async function fetchFreshShipmentList(
  token: AccessToken,
  params: {
    fertilizationDate: string;
    fertilizationSiteId: string;
    fertilizationSiteName: string;
  },
): Promise<FreshShipmentListItem[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', params.fertilizationDate);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  searchParams.set('fertilizationSiteName', params.fertilizationSiteName);
  return fetchGet<FreshShipmentListItem[]>(
    token,
    'fresh-shipment-list',
    searchParams,
  );
}

async function fetchFertilizationList(
  token: AccessToken,
  params: {
    fertilizationDate: string;
    fertilizationSiteId: string;
    shipGroupId: string;
  },
): Promise<TOA_GetFreshShipmentFertList[]> {
  const searchParams = new URLSearchParams(params);
  return fetchGet<TOA_GetFreshShipmentFertList[]>(
    token,
    'fertilization-list',
    searchParams,
  );
}

async function fetchFreshShipmentQC(
  token: AccessToken,
  params: {
    fertilizationDate: string;
    fertilizationSiteId: string;
    shipGroupIds: string[];
  },
): Promise<FertilizationQCResponse> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', params.fertilizationDate);
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  for (const id of params.shipGroupIds) {
    searchParams.append('shipGroupIds', id);
  }
  return fetchGet<FertilizationQCResponse>(token, 'qc', searchParams);
}

async function fetchEstimationHeaderVal(
  token: AccessToken,
  shipGroupId: string,
): Promise<number> {
  return fetchGet<number>(token, `estimation-header/${shipGroupId}`);
}

export function fertPredictionTechQueryOptions(
  token: AccessToken,
  fertilizationId: string,
) {
  return queryOptions({
    queryKey: ['fresh-shipment', 'fert-prediction-tech', fertilizationId],
    async queryFn() {
      const url = `${getBaseUrl()}/api/v1/FreshShipment/fert-prediction-tech/${fertilizationId}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: getHeaders(token),
      });
      if (response.status === 204) return null;
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      return (await response.json()) as EquipmentItem;
    },
    enabled: !!token,
  });
}

async function fetchShipmentGroupMasters(
  token: AccessToken,
  params: {
    fertilizationSiteId: string;
    shipGroupId: string;
  },
): Promise<TOA_GetFreshShipmentShipGroupMasters[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
  searchParams.set('shipGroupId', params.shipGroupId);
  return fetchGet<TOA_GetFreshShipmentShipGroupMasters[]>(
    token,
    'ship-group-masters',
    searchParams,
  );
}

export const shipGroupListOptions = (
  token: AccessToken,
  params: {
    fertilizationDate: string;
    fertilizationSiteId: string;
  },
) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'ship-group-list', params],
    async queryFn() {
      return fetchShipGroupList(token, params);
    },
    enabled: !!token,
  });

export const freshShipmentListOptions = (
  token: AccessToken,
  params: {
    fertilizationDate: string;
    fertilizationSiteId: string;
    fertilizationSiteName: string;
  },
) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'fresh-shipment-list', params],
    async queryFn() {
      return fetchFreshShipmentList(token, params);
    },
    enabled: !!token,
  });

export type FreshShipmentFertListParams = {
  fertilizationDate: string;
  fertilizationSiteId: string;
  shipGroupId: string;
};

export const fertilizationListOptions = (
  token: AccessToken,
  params: FreshShipmentFertListParams,
) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'fertilization-list', params],
    async queryFn() {
      return fetchFertilizationList(token, params);
    },
    enabled: !!token,
  });

export const freshShipmentFertListQueryOptions = fertilizationListOptions;

export const freshShipmentQCOptions = (
  token: AccessToken,
  params: {
    fertilizationDate: string;
    fertilizationSiteId: string;
    shipGroupIds: string[];
  },
) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'qc', params],
    async queryFn() {
      return fetchFreshShipmentQC(token, params);
    },
    enabled: !!token,
  });

export const estimationHeaderValOptions = (
  token: AccessToken,
  shipGroupId: string,
) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'estimation-header', shipGroupId],
    async queryFn() {
      return fetchEstimationHeaderVal(token, shipGroupId);
    },
    enabled: !!token,
  });

export const shipmentGroupMastersOptions = (
  token: AccessToken,
  params: {
    fertilizationSiteId: string;
    shipGroupId: string;
  },
) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'ship-group-masters', params],
    async queryFn() {
      return fetchShipmentGroupMasters(token, params);
    },
    enabled: !!token,
  });

export const freshStrawFertilizationsV2Options = (
  token: AccessToken,
  params: {
    fertilizationDate: string;
    fertilizationSite: string;
    freshShipmentId: string;
  },
) =>
  queryOptions({
    queryKey: ['fresh-straw', 'fertilizations', 'v2', params],
    async queryFn() {
      return fetchFreshStrawFertilizationsV2(token, params);
    },
    enabled: !!token,
  });

export function useUpdateQcFreshShipmentRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (input: UpdateQcRequest) =>
      fetchMutation<MutationResult>(token, 'update-qc', 'POST', input),
  });
}

export const useUpdateQcFreshShipmentRest =
  useUpdateQcFreshShipmentRestMutation;

export function useUpdateFertTransactionRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: UpdateFertFieldRequest) =>
      fetchMutation<MutationResult>(
        token,
        `fertilization/${params.id}`,
        'PUT',
        {
          field: params.field,
          value: params.value,
          updatePredictedComments: params.updatePredictedComments,
        },
      ),
  });
}

export function useUpdateDSTransactionRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: UpdateDSFieldRequest) =>
      fetchMutation<MutationResult>(
        token,
        `donor-schedule/${params.id}`,
        'PUT',
        {
          field: params.field,
          value: params.value,
          updatePredictedComments: params.updatePredictedComments,
          isIvf: params.isIvf,
        },
      ),
  });
}

export function useUpdateFreshShipmentTransactionRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: UpdateFieldRequest) =>
      fetchMutation<MutationResult>(
        token,
        `fresh-shipment/${params.id}`,
        'PUT',
        {
          field: params.field,
          value: params.value,
        },
      ),
  });
}

export function useUpdateShipmentGroupTransactionRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: UpdateFieldRequest) =>
      fetchMutation<MutationResult>(
        token,
        `shipment-group/${params.id}`,
        'PUT',
        {
          field: params.field,
          value: params.value,
        },
      ),
  });
}

export function useUpdateIncubatorAssignmentRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: UpdateIncubatorAssignmentRequest) =>
      fetchMutation<MutationResult>(
        token,
        `incubator-assignment/${params.fertilizationId}`,
        'PUT',
        { shipGroupMasterId: params.shipGroupMasterId },
      ),
  });
}

export function useSignOffRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: SignOffRequest) =>
      fetchMutation<MutationResult>(token, 'sign-off-email', 'POST', params),
  });
}

export function useUndoShipmentRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (freshShipmentId: string) =>
      fetchMutation<MutationResult>(
        token,
        `undo-shipment/${freshShipmentId}`,
        'POST',
      ),
  });
}

export function useCombineShipmentsRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: CombineShipmentsRequest) =>
      fetchMutation<MutationResult>(token, 'combine-shipments', 'POST', params),
  });
}

export function useCreateCommDairyExcelRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: {
      fertilizationDate: string;
      fertilizationSiteId: string;
      shipGroupId: string;
      d5: boolean;
    }) => {
      const searchParams = new URLSearchParams();
      searchParams.set('fertilizationDate', params.fertilizationDate);
      searchParams.set('fertilizationSiteId', params.fertilizationSiteId);
      searchParams.set('shipGroupId', params.shipGroupId);
      searchParams.set('d5', String(params.d5));
      return fetchBlob(token, 'comm-dairy-excel', searchParams);
    },
  });
}

export function useSendCommDairyEmailRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: SendCommDairyEmailRequest) =>
      fetchMutation<{ success: boolean }>(
        token,
        'comm-dairy-email',
        'POST',
        params,
      ),
  });
}

export function useMatingLabelsRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: MatingLabelsRequest) => {
      const request = {
        fertilizationDate: format(
          new Date(params.fertilizationDate),
          'yyyy-MM-dd',
        ),
        site: params.site,
        shipGroups: params.shipGroups,
      };
      return fetchBlobReport(token, 'mating-labels', request);
    },
  });
}

export function useMatingLabelsDupesRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (params: MatingLabelsRequest) => {
      const request = {
        fertilizationDate: format(
          new Date(params.fertilizationDate),
          'yyyy-MM-dd',
        ),
        site: params.site,
        shipGroups: params.shipGroups,
      };
      return fetchBlobReport(token, 'mating-labels-dupes', request);
    },
  });
}

export function useVerifyFreshShipmentMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async (params: {
      freshShipmentId: string;
      verified: boolean;
    }) => {
      const url = `${getBaseUrl()}/api/fresh-straw/fresh-shipments/${params.freshShipmentId}/verify`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders(token),
        body: JSON.stringify({ verified: params.verified }),
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      return (await response.json()) as { success: boolean };
    },
  });
}

export const shipmentEstimationOptions = (
  token: AccessToken,
  freshShipmentId: string,
) =>
  queryOptions({
    queryKey: ['fresh-shipment', 'shipment-estimation', freshShipmentId],
    async queryFn() {
      return fetchGet<number | null>(
        token,
        `shipment-estimations/${freshShipmentId}`,
      );
    },
    enabled: !!token,
  });

export type FreshShipmentEmbryoImportResponse = {
  parsed: number;
  saved: number;
  skipped: number;
  updated: number;
  errors: string[];
};

export type FreshShipmentEmbryoImportArgs = {
  file: File;
};

async function getImportErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { message?: unknown };
    if (typeof body.message === 'string') {
      return body.message;
    }
  } catch {
    // Fall back to the status when the response has no JSON error body.
  }

  return `HTTP error! status: ${response.status}`;
}

export function useImportFreshEmbryosBatchV2Mutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async ({
      file,
    }: FreshShipmentEmbryoImportArgs): Promise<FreshShipmentEmbryoImportResponse> => {
      if (!token) throw new Error('No access token available. Please log in.');

      const formData = new FormData();
      formData.append('file', file);

      const url = `${getBaseUrl()}/api/fresh-straw/imports/fresh-embryos/batch/v2`;

      try {
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
          signal: AbortSignal.timeout(300_000),
        });

        if (!response.ok) {
          const text = await response.text();
          const err = new Error(text) as Error & { data: string };
          err.data = text;
          throw err;
        }
        return (await response.json()) as FreshShipmentEmbryoImportResponse;
      } catch (error: unknown) {
        if (error instanceof DOMException && error.name === 'TimeoutError') {
          const err = new Error(
            'Upload timed out after 5 minutes. The file may be too large — try splitting it into smaller batches.',
          ) as Error & { data: string };
          err.data =
            'Upload timed out after 5 minutes. The file may be too large — try splitting it into smaller batches.';
          throw err;
        }
        throw error;
      }
    },
  });
}

/**
 * Streaming fresh-embryo upload (batch/v3).
 *
 * The v2 endpoint returns one JSON body at the very end, so the browser sits on an idle
 * socket for the whole import and `AbortSignal.timeout` eventually kills it — the endless
 * spinner. v3 returns an NDJSON progress stream instead: bytes arrive immediately and at
 * least every 10 seconds, so nothing in the chain treats the connection as dead.
 *
 * Cancellation is an explicit AbortController wired to the toast's Cancel button rather
 * than a flat timeout, so a genuinely long-but-healthy import is never cut off.
 */
export function useImportFreshEmbryosBatchV3Mutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async ({
      file,
    }: FreshShipmentEmbryoImportArgs): Promise<FreshShipmentEmbryoImportResponse> => {
      if (!token) throw new Error('No access token available. Please log in.');

      const formData = new FormData();
      formData.append('file', file);

      const abortController = new AbortController();
      const progressToast = createProgressToast({
        title: 'Uploading fresh embryos',
        label: 'Processing rows',
        errorTitle: 'Upload failed',
        onCancel: () => abortController.abort(),
      });

      try {
        const response = await fetch(
          `${getBaseUrl()}/api/fresh-straw/imports/fresh-embryos/batch/v3`,
          {
            method: 'POST',
            credentials: 'include',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
            signal: abortController.signal,
          },
        );

        if (!response.ok) {
          throw new Error(await getImportErrorMessage(response));
        }

        for await (const event of readProgressStream<
          FreshShipmentEmbryoImportResponse,
          { message: string }
        >(response)) {
          switch (event.type) {
            case 'progress':
              progressToast.report(event.progress);
              break;
            case 'success':
              progressToast.close();
              return event.success;
            case 'error':
              throw new ProgressStreamError(event.error);
          }
        }

        throw new Error('Upload stream ended without a terminal event.');
      } catch (error) {
        if (isAbortError(error)) {
          progressToast.cancelled();
          throw error;
        }

        const message =
          error instanceof ProgressStreamError
            ? (error.error as { message: string }).message
            : error instanceof Error
              ? error.message
              : 'Upload failed.';

        // The upload dialog owns error presentation (it also renders the per-row error
        // list), so just retire the progress toast here rather than showing a second one.
        progressToast.close();

        const err = new Error(message) as Error & { data: string };
        err.data = message;
        throw err;
      }
    },
  });
}

export function useAutoVerifyIfReconciledMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async (
      fertilizationId: string,
    ): Promise<{ verified: boolean }> => {
      const url = `${getBaseUrl()}/api/fresh-straw/fertilizations/${fertilizationId}/auto-verify-if-reconciled`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders(token),
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      return (await response.json()) as { verified: boolean };
    },
  });
}
